import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import { TableSearchbar } from '@/components/TableSearchbar'
import { TableTab } from '@/components/TableTab'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'
import { useFeatures } from '@/contexts/root-store-context'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import {
  ClientUserManagementTableHeaderRow,
  TableUserCount,
} from '@/features/client-user-management/components/ClientUserManagementTable'
import { ClientUserManagementTableRowContainer } from '@/features/client-user-management/components/ClientUserManagementTableRow/ClientUserManagementTableRowContainer'
import { type AdvisorClientsTabViewModel } from './AdvisorClientsTabViewModel'

export interface AdvisorClientsTabContainerProps {
  vm: AdvisorClientsTabViewModel
}

export const AdvisorClientsTabContainer = observer(
  function AdvisorClientsTabContainer({ vm }: AdvisorClientsTabContainerProps) {
    const navigate = useNavigate()
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    return (
      <TableTab
        toolbar={
          <TableUserCount
            count={vm.userSearchVm.totalUsers}
            isShowingInactiveUsers={vm.userSearchVm.userInactiveFilter.value}
            onInactiveChange={vm.userSearchVm.userInactiveFilter.change}
            noun="clients"
          />
        }
      >
        <TableSearchbar
          searchText={vm.userSearchVm.searchText.value}
          onSearchTextChange={vm.userSearchVm.searchText.change}
        />
        {!vm.userSearchVm.shouldShowEmptyState ? (
          <VirtualTableContainer
            overscan={50}
            scroller={vm.userSearchVm.scrollProvider}
            data={vm.userSearchVm.users}
            headerContent={() => (
              <ClientUserManagementTableHeaderRow columns={vm.columns} />
            )}
            itemContent={(_index, user) => (
              <ClientUserManagementTableRowContainer
                key={user.publicId}
                user={user}
              />
            )}
            onPressItemRow={(user: SearchableUser) =>
              navigate(vm.getUserDetailsLinkForUser(user))
            }
          />
        ) : (
          <NoResultsEmptyState
            corporateMode={shellInfo.corporateMode}
            isSearchFilterApplied={vm.userSearchVm.isSearchFilterApplied}
            onClearSearchFilter={vm.userSearchVm.clearFilters}
            title="No assigned clients"
          />
        )}
      </TableTab>
    )
  },
)
